import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import MarketingLayout from '../layouts/MarketingLayout'
import DashboardLayout from '../layouts/DashboardLayout'
import AdminLayout from '../layouts/AdminLayout'
import NotFound from '../pages/NotFound'

// Critical path pages (loaded immediately)
import Home from '../pages/Home'
import Login from '../pages/Login'
import Pricing from '../pages/Pricing'
import Download from '../pages/Download'
import VerifyMagicLink from '../pages/VerifyMagicLink'
import PurchaseSuccess from '../pages/PurchaseSuccess'

// Lazy-loaded marketing pages (less frequently accessed)
const FAQ = lazy(() => import('../pages/FAQ'))
const Contact = lazy(() => import('../pages/Contact'))
const Docs = lazy(() => import('../pages/Docs'))
const GettingStarted = lazy(() => import('../pages/docs/GettingStarted'))
const AccountRecovery = lazy(() => import('../pages/support/AccountRecovery'))
const TermsOfUse = lazy(() => import('../pages/TermsOfUse'))
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'))
const AboutUs = lazy(() => import('../pages/AboutUs'))

// Lazy-loaded dashboard pages (behind authentication)
const DashboardOverview = lazy(() => import('../pages/dashboard/Overview'))
const DashboardLicenses = lazy(() => import('../pages/dashboard/Licenses'))
const DashboardLicenseDetail = lazy(() => import('../pages/dashboard/LicenseDetail'))
const DashboardProfile = lazy(() => import('../pages/dashboard/settings/Profile'))
const DashboardSecurity = lazy(() => import('../pages/dashboard/settings/Security'))
const DashboardBilling = lazy(() => import('../pages/dashboard/settings/Billing'))
const DashboardSettingsLicenses = lazy(() => import('../pages/dashboard/settings/Licenses'))

// Lazy-loaded company pages (behind authentication, company membership)
const CompanyProfile = lazy(() => import('../pages/company/CompanyProfile'))
const CompanyMembers = lazy(() => import('../pages/company/CompanyMembers'))
const CompanyCredits = lazy(() => import('../pages/company/CompanyCredits'))
const CompanyTransactions = lazy(() => import('../pages/company/CompanyTransactions'))

// Lazy-loaded admin pages (behind admin authorization)
const AdminAnalytics = lazy(() => import('../pages/admin/Analytics'))
const AdminUserList = lazy(() => import('../pages/admin/UserList'))
const AdminUserDetail = lazy(() => import('../pages/admin/UserDetail'))
const AdminTransactions = lazy(() => import('../pages/admin/Transactions'))
const AdminTransactionDetail = lazy(() => import('../pages/admin/TransactionDetail'))
const AdminCompanyList = lazy(() => import('../pages/admin/CompanyList'))
const AdminCompanyDetail = lazy(() => import('../pages/admin/CompanyDetail'))
const AdminAuditLogs = lazy(() => import('../pages/admin/AuditLogs'))
const AdminFeatureFlags = lazy(() => import('../pages/admin/FeatureFlags'))
const AdminStaff = lazy(() => import('../pages/admin/AdminStaff'))
const AdminSystem = lazy(() => import('../pages/admin/System'))

/**
 * Page loading fallback - Derek-friendly loading indicator
 */
function PageLoader() {
  return (
    <div className="page-loader" aria-label="Loading page">
      <div className="page-loader__spinner" aria-hidden="true" />
      <p className="page-loader__text">Loading...</p>
    </div>
  )
}

/**
 * Suspense wrapper for lazy-loaded routes
 */
function LazyRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

const router = createBrowserRouter([
  // Marketing pages (public)
  {
    path: '/',
    element: <MarketingLayout />,
    errorElement: <NotFound />,
    children: [
      // Critical path - loaded immediately
      { index: true, element: <Home /> },
      { path: 'pricing', element: <Pricing /> },
      { path: 'download', element: <Download /> },
      { path: 'login', element: <Login /> },
      { path: 'verify', element: <VerifyMagicLink /> },
      { path: 'purchase/success', element: <PurchaseSuccess /> },
      // Lazy-loaded - less frequently accessed
      { path: 'faq', element: <LazyRoute><FAQ /></LazyRoute> },
      { path: 'contact', element: <LazyRoute><Contact /></LazyRoute> },
      { path: 'docs', element: <LazyRoute><Docs /></LazyRoute> },
      { path: 'docs/getting-started', element: <LazyRoute><GettingStarted /></LazyRoute> },
      { path: 'account-recovery', element: <LazyRoute><AccountRecovery /></LazyRoute> },
      { path: 'terms', element: <LazyRoute><TermsOfUse /></LazyRoute> },
      { path: 'privacy', element: <LazyRoute><PrivacyPolicy /></LazyRoute> },
      { path: 'about', element: <LazyRoute><AboutUs /></LazyRoute> },
    ],
  },
  // Dashboard pages (authenticated) - all lazy-loaded
  {
    path: '/dashboard',
    element: <DashboardLayout />,
    children: [
      { index: true, element: <LazyRoute><DashboardOverview /></LazyRoute> },
      { path: 'licenses', element: <LazyRoute><DashboardLicenses /></LazyRoute> },
      { path: 'licenses/:licenseKey', element: <LazyRoute><DashboardLicenseDetail /></LazyRoute> },
      { path: 'settings/profile', element: <LazyRoute><DashboardProfile /></LazyRoute> },
      { path: 'settings/security', element: <LazyRoute><DashboardSecurity /></LazyRoute> },
      { path: 'settings/billing', element: <LazyRoute><DashboardBilling /></LazyRoute> },
      { path: 'settings/licenses', element: <LazyRoute><DashboardSettingsLicenses /></LazyRoute> },
      // Company pages (company membership required - enforced in components)
      { path: 'company', element: <LazyRoute><CompanyProfile /></LazyRoute> },
      { path: 'company/members', element: <LazyRoute><CompanyMembers /></LazyRoute> },
      { path: 'company/credits', element: <LazyRoute><CompanyCredits /></LazyRoute> },
      { path: 'company/transactions', element: <LazyRoute><CompanyTransactions /></LazyRoute> },
    ],
  },
  // Admin pages (admin authorization required) - all lazy-loaded
  {
    path: '/admin',
    element: <AdminLayout />,
    children: [
      { index: true, element: <LazyRoute><AdminAnalytics /></LazyRoute> },
      { path: 'users', element: <LazyRoute><AdminUserList /></LazyRoute> },
      { path: 'users/:userId', element: <LazyRoute><AdminUserDetail /></LazyRoute> },
      { path: 'transactions', element: <LazyRoute><AdminTransactions /></LazyRoute> },
      { path: 'transactions/:transactionId', element: <LazyRoute><AdminTransactionDetail /></LazyRoute> },
      { path: 'companies', element: <LazyRoute><AdminCompanyList /></LazyRoute> },
      { path: 'companies/:companyId', element: <LazyRoute><AdminCompanyDetail /></LazyRoute> },
      { path: 'audit', element: <LazyRoute><AdminAuditLogs /></LazyRoute> },
      { path: 'staff', element: <LazyRoute><AdminStaff /></LazyRoute> },
      { path: 'feature-flags', element: <LazyRoute><AdminFeatureFlags /></LazyRoute> },
      { path: 'system', element: <LazyRoute><AdminSystem /></LazyRoute> },
    ],
  },
  // Fallback
  { path: '*', element: <NotFound /> },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}

export default AppRouter
